/**
 * Modal — the ONE modal surface for the platform (#387 / UX-02).
 *
 * Before this existed, a repo-wide search for `aria-modal` returned exactly one
 * hit: the console's mobile nav drawer. Every other overlay — the comms case peek,
 * the comms composer, the Lookup dialog — was a `<div>` over the page with a
 * backdrop click handler. For a keyboard or screen-reader user those did not open:
 * focus stayed behind the scrim, Tab walked the page underneath, Escape did
 * nothing, and closing dropped focus on `<body>`.
 *
 * ── Why this WRAPS Radix Dialog rather than hand-rolling ───────────────────────
 *
 * Radix Dialog was already a console dependency (the ⌘K palette), and its
 * transitive weight — react-remove-scroll, aria-hidden — already arrives in
 * @solum/ui via @radix-ui/react-select. So the marginal cost is close to zero,
 * and it buys four things a hand-rolled trap does not have:
 *
 *   • `aria-hidden` on everything OUTSIDE the dialog. The AppShell trap (the
 *     working reference) does not do this, so a screen reader can still read the
 *     page behind it with a virtual cursor even though Tab is trapped.
 *   • A LAYER STACK. Escape closes the topmost dialog only. This repo needs it:
 *     Lookup opens from inside forms that are themselves in dialogs, and the
 *     hand-rolled version of that is exactly the `e.stopPropagation()` +
 *     `{ capture: true }` scattering visible in Lookup today.
 *   • Scroll lock with scrollbar-width compensation (the layout does not jump).
 *   • Focus GUARDS — sentinel nodes that catch a Tab out of the portal even when
 *     focus started outside the content, which a keydown-only trap misses.
 *
 * Two things are deliberately NOT delegated, both measured rather than assumed:
 *
 *   1. `aria-modal`. Radix does not set it (it relies on aria-hidden instead).
 *      #387 asks for it, and the console's own e2e already asserts it on the
 *      drawer, so it is set explicitly here.
 *   2. FOCUS RESTORATION. Radix restores via `onCloseAutoFocus` during its own
 *      unmount sequencing. Every call site in this repo mounts conditionally
 *      (`{open && <Modal/>}`), which tears the Root down first — measured: focus
 *      landed on `<body>`, not on the trigger. So restoration is owned here, the
 *      same way AppShell owns it, and Radix's is suppressed so the two cannot
 *      fight.
 *
 * ── The accessible name is REQUIRED ───────────────────────────────────────────
 *
 * `label` is a required prop and is also checked at runtime. Two of the surfaces
 * this replaces shipped `role="dialog"` with no name at all, which a screen
 * reader announces as the single word "dialog".
 */
import { useEffect, useRef, type ReactNode, type RefObject } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { cx } from './cx'
import { OverlayPortalContext } from './OverlayPortal'
import s from './Modal.module.css'

export interface ModalProps {
  /**
   * The accessible name — REQUIRED, and non-empty. Rendered as a visually hidden
   * `<h2>` so it is the dialog's `aria-labelledby` target. If the panel also
   * shows a visible heading, pass the same words; the name is announced once.
   */
  label: string
  /** Called on Escape, on a click outside the panel, and by the caller's own close control. */
  onClose: () => void
  children: ReactNode

  /** Rendered BESIDE the panel, inside the same focus scope (Lookup's quick-create). */
  aside?: ReactNode
  /**
   * Chooses the default classes only. Overridden entirely by the *ClassName props.
   *
   * `sheet` is a RIGHT-EDGE full-height side drawer — measured, at all eight of
   * its call sites. `bottom` is the phone bottom sheet #2328 §3 names as "the
   * established bottom-sheet pattern", added rather than reinterpreting `sheet`,
   * so no existing surface moves by a pixel.
   */
  variant?: 'center' | 'sheet' | 'bottom'
  /** Panel class — pass the call site's own class to keep its existing look. */
  className?: string
  /** Backdrop class. */
  overlayClassName?: string
  /** Only used when `aside` is set: the flex row holding panel + aside. */
  wrapClassName?: string
  width?: number
  testid?: string
  /**
   * Focus this on open instead of the first tabbable thing. Needed where the
   * first tabbable is a close button but the useful target is a field — the
   * comms composer's title input, for one.
   */
  initialFocus?: RefObject<HTMLElement | null>
  /**
   * Where focus goes when this closes — a STABLE element that outlives the
   * modal, overriding the captured-`activeElement` fallback below.
   *
   * ── the defect this exists for (#1466 F-30B, ADR F-30 decision 3) ─────────
   * The fallback captures `document.activeElement` at MOUNT, and "mounted" is
   * "open" for this component. That is correct whenever the launcher is still
   * on screen. It is wrong for a modal launched from a DISCLOSURE, because the
   * disclosure closes first: `UserMenu` calls `setOpen(false)` before
   * `onSelect`, so the item that was focused is already unmounted and
   * `activeElement` is `<body>` by the time this effect runs. Measured in every
   * profile on the portal's "Email notifications" sheet — closing it returned
   * focus to `<body>`, i.e. to nowhere (#1466 comment 42636 finding 4).
   *
   * The fix is the one the ruling names and not the other one: keeping the menu
   * OPEN under the sheet would leave an interactive menu beneath a modal, which
   * is a worse defect than the one being fixed. So the menu still closes, and
   * the host passes the launcher that SURVIVES it — for the portal, the user
   * menu's own trigger.
   *
   * Optional, and the fallback is preserved: a host that does not pass one gets
   * exactly the behaviour it had. Where the ref is present but empty at close
   * time (the target itself unmounted), the captured element is used instead —
   * a restoration that silently goes nowhere is the bug, not the remedy.
   */
  restoreFocusTo?: RefObject<HTMLElement | null>
  /** Escape and outside-click close. Default true. */
  dismissable?: boolean
}

/* One table, two readers, three variants. A nested ternary for the third
   variant is where the overlay and the panel start disagreeing about which one
   they are in — they are two halves of one decision and this is that decision. */
const OVERLAY_CLASS: Record<NonNullable<ModalProps['variant']>, string> = {
  center: s.overlayCenter!, sheet: s.overlaySheet!, bottom: s.overlayBottom!,
}
const PANEL_CLASS: Record<NonNullable<ModalProps['variant']>, string> = {
  center: s.panelCenter!, sheet: s.panelSheet!, bottom: s.panelBottom!,
}

export function Modal({
  label, onClose, children, aside, variant = 'center',
  className, overlayClassName, wrapClassName, width, testid, initialFocus,
  restoreFocusTo, dismissable = true,
}: ModalProps) {
  // Runtime half of the required-name rule. The type stops a TypeScript caller;
  // this stops a JS caller, a spread of a wider object, and `label={''}` — which
  // typechecks perfectly and produces exactly the nameless dialog we are fixing.
  if (typeof label !== 'string' || label.trim() === '') {
    throw new Error('<Modal> requires a non-empty `label` — a dialog with no accessible name is announced as just "dialog".')
  }

  // Focus restoration (see the header). Captured on mount because "mounted" IS
  // "open" for this component; returned on unmount, so it survives closing by
  // Escape, by the backdrop, or by the caller unmounting us outright.
  const restoreTo = useRef<HTMLElement | null>(null)
  /* DatePicker and Lookup are custom portals. Their nearest safe portal root is
     this modal's own Content, not document.body outside Radix's inert layer. */
  const nestedPortalTarget = useRef<HTMLDivElement>(null)
  const nestedEscapeLayers = useRef(0)
  // Read through a ref so the cleanup below never closes over a stale prop —
  // the whole value of `restoreFocusTo` is that it is resolved at CLOSE time,
  // when the caller's own ref has settled.
  const restoreTarget = useRef(restoreFocusTo)
  restoreTarget.current = restoreFocusTo
  useEffect(() => {
    restoreTo.current = document.activeElement as HTMLElement | null
    return () => {
      // The explicit target wins; the captured element remains the fallback,
      // including when the explicit one has itself gone away.
      const explicit = restoreTarget.current?.current
      if (explicit && explicit.isConnected) { explicit.focus?.(); return }
      restoreTo.current?.focus?.()
    }
  }, [])

  const panel = (
    <div
      ref={nestedPortalTarget}
      className={className ?? cx(s.panel, PANEL_CLASS[variant])}
      style={width != null ? { width } : undefined}
      data-testid={testid}
    >
      <OverlayPortalContext.Provider value={{ target: nestedPortalTarget, escapeLayers: nestedEscapeLayers }}>
        {/* Radix wires this up as aria-labelledby. Kept inside the panel (not the
            wrap) so it travels with the content in both structures. */}
        <Dialog.Title className={s.srOnly}>{label}</Dialog.Title>
        {children}
      </OverlayPortalContext.Provider>
    </div>
  )

  /* Read through a REF, not from the closure. Radix's DismissableLayer holds
     these handlers behind `useEffectEvent` and registers ONE document-capture
     listener; a host that flips `dismissable` at runtime — the navigation
     editor does, while a row is held in grab mode — was still dismissed on
     Escape, while a constant `dismissable={false}` was honoured. Measured both
     ways in a real browser, with the host's state confirmed committed to the
     DOM at the moment of the keypress (#1466 A review). The ref makes the
     answer current whatever Radix cached. */
  const live = useRef(dismissable)
  live.current = dismissable

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) onClose() }}>
      <Dialog.Portal>
        {/* Content NESTED inside Overlay, not beside it: the call sites style the
            backdrop as the flex container that positions the panel (centered, or
            pinned to the right edge). Siblings would lose that positioning. */}
        <Dialog.Overlay className={overlayClassName ?? cx(s.overlay, OVERLAY_CLASS[variant])}>
          <Dialog.Content
            asChild
            // Radix omits aria-modal on purpose; #387 asks for it and the console
            // e2e asserts it. Harmless alongside Radix's aria-hidden approach.
            aria-modal
            // Radix points aria-describedby at a Description we do not render,
            // which leaves a dangling IDREF and a dev warning.
            aria-describedby={undefined}
            onOpenAutoFocus={initialFocus ? (e) => { e.preventDefault(); initialFocus.current?.focus() } : undefined}
            // Restoration is ours (see the header) — stop Radix doing a second,
            // differently-timed one.
            onCloseAutoFocus={(e) => e.preventDefault()}
            /* Always a handler, branching INSIDE it, never `undefined ? :` on
               the prop. A host that flips `dismissable` at runtime — the nav
               editor does, while a row is held in grab mode — was still
               dismissed on Escape when the prop went from undefined to a
               function, while a constant `dismissable={false}` worked.
               Measured both ways in a real browser (#1466 A review). */
            onEscapeKeyDown={(e) => {
              if (!live.current || nestedEscapeLayers.current > 0) e.preventDefault()
            }}
            onPointerDownOutside={(e) => { if (!live.current) e.preventDefault() }}
            onInteractOutside={(e) => { if (!live.current) e.preventDefault() }}
          >
            {aside ? <div className={wrapClassName ?? s.wrap}>{panel}{aside}</div> : panel}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
